import { Component, DestroyRef, EventEmitter, Input, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayLockService } from '../../../services/overlay-lock.service';
import { CelebrationDatesService, DatesValues } from '../../../services/celebration-dates.service';
import { DatesNeeded } from '../../celebration-dates/celebration-dates.rules';
import { BreakyComponent } from '../breaky/breaky.component';
import { CelebrationDatesFieldsComponent } from '../celebration-dates-fields/celebration-dates-fields.component';

/**
 * Aviso al iniciar sesión a quien aún no registró su cumpleaños o su aniversario en la empresa (antes no era un
 * dato que se pidiera). Pide solo lo que falta; «Más tarde» lo pospone un día.
 */
@Component({
  selector: 'app-dates-prompt-modal',
  standalone: true,
  imports: [CommonModule, BreakyComponent, CelebrationDatesFieldsComponent],
  template: `
    <div class="dp-backdrop" role="dialog" aria-modal="true" aria-labelledby="dp-title">
      <div class="dp-card">
        <ng-container *ngIf="!done; else thanks">
          <app-breaky pose="wave" [size]="64" motion="bounce"></app-breaky>
          <h2 id="dp-title">¡Te faltó registrar algo!</h2>
          <p class="dp-lead">{{ lead }} <strong>Por favor completa estos datos.</strong></p>

          <app-celebration-dates-fields
            [showBirthday]="needed.birthday"
            [showHire]="needed.hire"
            (valuesChange)="onValues($event)"
          ></app-celebration-dates-fields>

          <p class="dp-error" *ngIf="error" role="alert">{{ error }}</p>

          <button type="button" class="dp-save" [disabled]="!canSave || saving" (click)="save()">
            {{ saving ? 'Guardando…' : 'Guardar' }}
          </button>
          <button type="button" class="dp-later" [disabled]="saving" (click)="later()">Más tarde</button>
          <p class="dp-foot">Así tu equipo podrá celebrarte. Puedes cambiarlo cuando quieras en tu Perfil.</p>
        </ng-container>

        <ng-template #thanks>
          <app-breaky pose="happy" [size]="96" motion="bounce"></app-breaky>
          <h2 id="dp-title">¡Gracias!</h2>
          <p class="dp-lead">Ya registramos tus datos.</p>
          <button type="button" class="dp-save" (click)="finish()">Listo</button>
        </ng-template>
      </div>
    </div>
  `,
  styles: [
    `
      :host { display: contents; }
      .dp-backdrop { position: fixed; inset: 0; z-index: 10000; display: flex; align-items: center; justify-content: center; padding: 16px; background: rgba(2, 6, 23, 0.78); backdrop-filter: blur(4px); }
      .dp-card { position: relative; overflow-x: hidden; overflow-y: auto; width: 100%; max-width: 400px; max-height: 92vh; padding: 14px 16px 12px; display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; color: #fff; border-radius: 22px; background: linear-gradient(160deg, #1d2d5a, #0b132b); border: 1px solid #2a3d78; box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.1); }
      .dp-card > * { position: relative; z-index: 1; }
      .dp-card::after { content: ''; position: absolute; right: -34px; top: -38px; width: 120px; height: 120px; border-radius: 50%; background: rgba(255, 255, 255, 0.05); pointer-events: none; }
      h2 { margin: 0; font-size: 1.2rem; font-weight: 900; }
      .dp-lead { margin: 0; font-size: 0.85rem; line-height: 1.4; color: #e2e8f0; }
      app-celebration-dates-fields { width: 100%; margin-top: 4px; }
      .dp-error { margin: 0; font-size: 0.78rem; font-weight: 700; color: #fca5a5; }
      .dp-save { width: 100%; min-height: 48px; border: 0; border-radius: 16px; font-weight: 900; font-size: 0.82rem; text-transform: uppercase; color: #0b131e; background: linear-gradient(135deg, #a6ff00, #10b981); box-shadow: 0 5px 0 #0a7a55; cursor: pointer; }
      .dp-save:active:not([disabled]) { transform: translateY(3px); box-shadow: 0 2px 0 #0a7a55; }
      .dp-save[disabled] { opacity: 0.5; cursor: default; }
      .dp-later { width: 100%; min-height: 44px; border-radius: 14px; font-weight: 700; font-size: 0.8rem; color: #cbd5e1; background: #0b132b; border: 1px solid #2a3d78; cursor: pointer; box-shadow: 0 4px 0 rgba(0, 0, 0, 0.35); }
      .dp-foot { margin: 0; font-size: 0.68rem; color: #94a3b8; }
      @media (prefers-reduced-motion: reduce) { .dp-save:active { transform: none; } }
    `
  ]
})
export class DatesPromptModalComponent {
  private readonly overlayHold = inject(OverlayLockService).hold(inject(DestroyRef));
  private readonly service = inject(CelebrationDatesService);

  @Input({ required: true }) needed!: DatesNeeded;
  @Input({ required: true }) userId!: string;
  /** `saved` al completar y `later` al posponer. */
  @Output() closeModal = new EventEmitter<'saved' | 'later'>();

  values: DatesValues = { day: null, month: null, year: null, hireDate: '' };
  valid = true;
  saving = false;
  done = false;
  error = '';

  get lead(): string {
    if (this.needed.birthday && this.needed.hire) return 'Te faltó registrar tu cumpleaños y tu aniversario en la empresa.';
    return this.needed.birthday ? 'Te faltó registrar tu cumpleaños.' : 'Te faltó registrar tu aniversario en la empresa.';
  }

  /** Hay algo que guardar (al menos una fecha completa) y todo lo escrito es válido. */
  get canSave(): boolean {
    const birth = this.needed.birthday && this.values.day !== null && this.values.month !== null;
    const hire = this.needed.hire && !!this.values.hireDate;
    return this.valid && (birth || hire);
  }

  onValues(e: { values: DatesValues; valid: boolean }): void {
    this.values = e.values;
    this.valid = e.valid;
    this.error = '';
  }

  async save(): Promise<void> {
    if (!this.canSave || this.saving) return;
    this.saving = true;
    this.error = '';
    const result = await this.service.save(this.values, this.needed);
    this.saving = false;
    const failure = result.birthdayError || result.hireError;
    if (failure) {
      this.error = failure;
      return;
    }
    this.done = true;
  }

  later(): void {
    this.service.snooze(this.userId);
    this.closeModal.emit('later');
  }

  finish(): void {
    this.closeModal.emit('saved');
  }
}
